import React from "react";
import { Link } from "react-router-dom";
import SiteShell from "@/components/SiteShell";
import Packages from "@/components/Packages";
import LeadForm from "@/components/LeadForm";
import SampleAdsCTASection from "@/components/SampleAdsCTASection";
import RotatingMaureenQuote from "@/components/RotatingMaureenQuote";
import { tourismList } from "@/data/tourism";
import {
  Globe,
  Wine,
  BedDouble,
  Ticket,
  Utensils,
  CalendarHeart,
  ArrowRight,
  MapPin,
  Radio,
  Smartphone,
  Sparkles,
  TrendingUp,
} from "lucide-react";

const ICONS = { Globe, Wine, BedDouble, Ticket, Utensils, CalendarHeart };

// Per-category proof / season meta — pulled from the real case data in
// `/app/frontend/src/data/tourism.js`. Keep in sync if those stats change.
const CATEGORY_META = {
  dmos: {
    peak: "Spring + Fall peaks",
    stat: "45,000+ visits in 12 months",
  },
  "wineries-breweries": {
    peak: "Spring → Holiday",
    stat: "30+ wineries within 30 miles",
  },
  "hotels-inns": {
    peak: "Peak + Shoulder",
    stat: "See Dark Skies campaign",
  },
  attractions: {
    peak: "Exhibits + School breaks",
    stat: "Statewide #1 visitation",
  },
  restaurants: {
    peak: "Year-round + Tourism peaks",
    stat: "Fast Casual Experts",
  },
  "events-festivals": {
    peak: "6 months → gate-open",
    stat: "Event Experts",
  },
};

const TourismHub = () => {
  return (
    <SiteShell>
      {/* HERO */}
      <section
        className="relative bg-[var(--af-navy)] text-[var(--af-cream)] grid-bg overflow-hidden"
        data-testid="hero-section"
      >
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-24 sm:pt-20 sm:pb-32 relative z-10">
          <div className="grid lg:grid-cols-12 gap-12 items-stretch">
            <div className="lg:col-span-7 fade-up">
              <div className="eyebrow mb-6">
                For Tourism Brands · Central Virginia HQ · Multi-State Reach
              </div>
              <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl mb-7">
                Your{" "}
                <span className="text-[var(--af-lime)]">first 90 days</span>{" "}
                with us. Then we go from there.
              </h1>
              <p className="text-lg sm:text-xl text-[var(--af-cream)]/75 max-w-2xl mb-7 leading-relaxed">
                A proven 90-day playbook for Virginia tourism brands: DMOs,
                wineries, hotels, attractions, restaurants, and festivals. We
                start with campaigns we know book the season, then we go from
                there. Years of tourism advertising experience means your ad
                dollars work while you focus on what you do best.
              </p>
              <div
                className="mb-8 inline-flex items-center gap-3 px-4 py-2.5 rounded-full border border-[var(--af-line)] bg-[var(--af-navy-soft)]/60"
                data-testid="hero-vifl-grant-badge"
              >
                <img
                  src="/media/clients/vifl-horizontal.png"
                  alt="Virginia Is For Lovers"
                  className="h-5 w-auto opacity-95 flex-shrink-0"
                />
                <div className="text-[11px] uppercase tracking-widest text-[var(--af-cream)]/80 font-medium">
                  VTC grant-aligned · MLP, ARPA, VIFL-compliant
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <a
                  href="#categories"
                  className="btn-lime"
                  data-testid="hero-cta-pick-category"
                >
                  Pick Your Category <ArrowRight size={14} />
                </a>
                <a
                  href="#lead-form"
                  className="btn-ghost"
                  data-testid="hero-cta-audit"
                >
                  Free Market Audit
                </a>
              </div>
              <div className="mt-12 flex flex-wrap gap-2.5">
                {[
                  "Drive overnight visitation",
                  "Increase destination awareness",
                  "Attract regional travelers",
                  "Extend visitor stays",
                  "Promote shoulder-season tourism",
                  "Support local tourism partners",
                  "Measure visitor engagement",
                ].map((o) => (
                  <span
                    key={o}
                    className="text-[11px] px-3 py-1.5 rounded-full border border-[var(--af-lime)]/40 bg-[var(--af-lime)]/10 text-[var(--af-cream)] font-medium tracking-wide"
                    data-testid={`hero-outcome-${o.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {o}
                  </span>
                ))}
              </div>
              <div className="mt-6 flex flex-wrap gap-x-7 gap-y-3 text-xs text-[var(--af-cream)]/55">
                <span className="flex items-center gap-2">
                  <Radio size={14} className="text-[var(--af-lime)]" />
                  Streaming TV
                </span>
                <span className="flex items-center gap-2">
                  <Smartphone size={14} className="text-[var(--af-lime)]" />
                  Mobile geo-targeting
                </span>
                <span className="flex items-center gap-2">
                  <MapPin size={14} className="text-[var(--af-lime)]" />
                  Multi-DMA campaigns
                </span>
                <span className="flex items-center gap-2">
                  <Sparkles size={14} className="text-[var(--af-lime)]" />
                  Campaign concepting + creative
                </span>
              </div>
            </div>

            {/* The big proof number */}
            <div className="lg:col-span-5 h-full">
              <div
                className="surface p-7 fade-up flex flex-col h-full"
                style={{ animationDelay: ".15s" }}
              >
                <div className="rounded-xl overflow-hidden border border-[var(--af-line)] mb-6 aspect-video bg-black">
                  <video
                    src="/media/reel/wanderlove-case-report.mp4"
                    poster="/media/reel/wanderlove-case-report.jpg"
                    autoPlay
                    loop
                    muted
                    playsInline
                    preload="auto"
                    onLoadedMetadata={(e) => {
                      e.currentTarget.play().catch(() => {});
                    }}
                    className="w-full h-full object-cover"
                    data-testid="hero-case-study-video"
                  />
                </div>
                <div className="flex items-start justify-between gap-4 mb-3">
                  <div className="eyebrow">
                    Case Study · Nelson County Tourism
                  </div>
                  <img
                    src="/media/clients/visit-nelson-county-vifl.png"
                    alt="Visit Nelson County · Virginia is for Lovers"
                    className="h-14 w-auto opacity-90 flex-shrink-0"
                    data-testid="hero-vnc-vifl-logo"
                  />
                </div>
                <div className="font-display text-7xl sm:text-8xl mt-3 text-[var(--af-lime)] leading-none">
                  45,000+
                </div>
                <div className="font-display text-xl mt-3">
                  website visits in 12 months
                </div>
                <p className="text-sm mt-4 text-[var(--af-cream)]/75 leading-relaxed">
                  Learn how Anne Flinn Advertising drove over 45,000 website
                  visits to Nelson County Tourism during the VTC Wanderlove
                  Campaign.
                </p>
                <Link
                  to="/tourism/case-studies"
                  className="text-xs text-[var(--af-lime)] mt-auto pt-6 inline-flex items-center gap-1 hover:underline underline-offset-4 font-medium"
                  data-testid="hero-case-studies-index-link"
                >
                  See all 5 tourism case studies <ArrowRight size={12} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* SMITHSONIAN PROOF BAR — now rotates through 6 Maureen Kelley quotes */}
      <RotatingMaureenQuote />

      {/* TOURISM GRANT FRAMEWORK — positions services through a tourism-outcomes lens */}
      <section
        className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
        data-testid="grant-framework-section"
      >
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">For Grant-Funded Tourism Initiatives</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-5 leading-tight">
              Our Tourism{" "}
              <span className="text-[var(--af-lime)]">Grant Framework.</span>
            </h2>
            <p className="text-base text-[var(--af-cream)]/75 leading-relaxed mb-6">
              A repeatable six-step process built specifically for DMOs,
              attractions, wineries, and event partners using tourism-grant
              dollars to drive measurable visitation outcomes.
            </p>
            <p className="text-sm text-[var(--af-cream)]/65 leading-relaxed">
              Our team has worked with tourism organizations, attractions,
              wineries, events, and destination partners across Virginia, and
              understands the reporting and documentation requirements that
              accompany grant-funded marketing initiatives.
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-4">
            {[
              { n: "01", t: "Define target visitor markets", d: "Identify the DMAs, demographics, and traveler segments most likely to convert into overnight visits or qualified spend." },
              { n: "02", t: "Build destination messaging", d: "Concept and produce on-brand creative that captures your destination story and emotional pull." },
              { n: "03", t: "Launch awareness campaign", d: "Streaming TV, paid social, mobile, and OTT, placed in market and live within days." },
              { n: "04", t: "Retarget interested travelers", d: "Re-engage warm visitors who clicked, viewed, or browsed, until they convert into trip planners." },
              { n: "05", t: "Capture visitor data", d: "First-party data, geo-attribution, and visitor-ID signals that prove the campaign moved real people." },
              { n: "06", t: "Report measurable results", d: "Grant-ready monthly + final reports with the data, screenshots, and narrative your funders expect." },
            ].map((s, i) => (
              <div
                key={i}
                className="surface p-5"
                data-testid={`grant-framework-step-${i + 1}`}
              >
                <div className="font-display text-[var(--af-lime)] text-2xl mb-2 leading-none">
                  {s.n}
                </div>
                <div className="font-display text-lg mb-2 leading-tight">
                  {s.t}
                </div>
                <div className="text-xs text-[var(--af-cream)]/65 leading-relaxed">
                  {s.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHO WE SERVE — Category Grid */}
      <section
        id="categories"
        className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
        data-testid="categories-section"
      >
        <div className="mb-14 max-w-3xl">
          <div className="flex items-center gap-4 mb-5">
            <div className="eyebrow">Who We Serve</div>
            <div className="h-px flex-1 bg-[var(--af-lime)]/25" />
          </div>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-5 leading-[1.05]">
            Six tourism playbooks.
            <br />
            <span className="text-[var(--af-lime)]">One boutique team.</span>
          </h2>
          <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-2xl leading-relaxed">
            Tourism is six industries in one. Each has its own season, its own
            visitor, and its own measure of success. Pick yours below for the
            messaging, the proof, and the campaign concepts we&apos;ve already
            run in it.
          </p>
        </div>

        <div
          className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6"
          data-testid="categories-grid"
        >
          {tourismList.map((c) => {
            const Icon = ICONS[c.icon] || Globe;
            const meta = CATEGORY_META[c.slug] || {};
            return (
              <Link
                key={c.slug}
                to={`/tourism/${c.slug}`}
                className="surface relative overflow-hidden p-6 sm:p-7 flex flex-col group hover:-translate-y-1 transition-transform duration-300"
                data-testid={`category-card-${c.slug}`}
              >
                {/* Header row: icon tile */}
                <div className="mb-6 relative z-10">
                  <div className="w-14 h-14 rounded-xl bg-gradient-to-br from-[var(--af-lime)]/25 to-[var(--af-lime)]/5 border border-[var(--af-lime)]/40 flex items-center justify-center group-hover:border-[var(--af-lime)] transition-colors">
                    <Icon size={26} className="text-[var(--af-lime)]" />
                  </div>
                </div>

                {/* Title + one-liner */}
                <div className="font-display text-2xl sm:text-[26px] mb-2 leading-tight relative z-10">
                  {c.name}
                </div>
                <p className="text-sm text-[var(--af-cream)]/70 mb-5 flex-1 leading-relaxed relative z-10">
                  {c.heroSub.split(".")[0]}.
                </p>

                {/* Chip stack: peak-moment + signature proof-stat */}
                <div className="flex flex-wrap gap-2 mb-5 relative z-10">
                  {meta.peak && (
                    <div
                      className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-[var(--af-cream)]/[0.06] border border-[var(--af-cream)]/15 text-[11px] text-[var(--af-cream)]/80"
                      data-testid={`category-peak-${c.slug}`}
                    >
                      <span className="text-[9px] uppercase tracking-widest text-[var(--af-lime)] font-eyebrow">
                        Peak
                      </span>
                      <span>{meta.peak}</span>
                    </div>
                  )}
                  {meta.stat && (
                    <div
                      className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--af-lime)]/10 border border-[var(--af-lime)]/30 text-[11px] group-hover:bg-[var(--af-lime)]/20 group-hover:border-[var(--af-lime)]/60 transition-colors"
                      data-testid={`category-stat-${c.slug}`}
                    >
                      <Sparkles
                        size={11}
                        className="text-[var(--af-lime)] shrink-0"
                      />
                      <span className="text-[var(--af-cream)] font-medium">
                        {meta.stat}
                      </span>
                    </div>
                  )}
                </div>

                {/* CTA */}
                <div className="text-sm font-semibold text-[var(--af-lime)] flex items-center gap-1.5 group-hover:gap-2.5 transition-all relative z-10">
                  See the {c.shortName} playbook
                  <ArrowRight
                    size={14}
                    className="group-hover:translate-x-1 transition-transform"
                  />
                </div>

                {/* Lime accent bar sweeps in on hover */}
                <div
                  className="absolute bottom-0 left-0 h-[3px] bg-[var(--af-lime)] w-0 group-hover:w-full transition-all duration-500 ease-out"
                  aria-hidden="true"
                />
              </Link>
            );
          })}
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <div className="dashed-divider" />
      </div>

      {/* PACKAGES */}
      <Packages vertical="tourism" />

      <SampleAdsCTASection
        label="Tourism"
        audience="tourism"
        testIdPrefix="tourism-hub-sample-ads"
      />

      {/* HOW WE'RE DIFFERENT */}
      <section
        className="px-5 sm:px-8 py-24 max-w-7xl mx-auto"
        data-testid="differentiators-section"
      >
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-4">Why Tourism Brands Pick Us</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-5">
              Every campaign is built for one outcome:
              <br />
              <span className="text-[var(--af-lime)]">stay-over visitors.</span>
            </h2>
            <p className="text-base text-[var(--af-cream)]/75 leading-relaxed">
              A boutique agency means you work directly with Anne and Collean,
              two senior strategists who've run campaigns for DMOs,
              breweries, theaters, and venues across Virginia. The principals
              own the strategy, the creative, and the outcome. Every week.
            </p>
          </div>

          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              {
                icon: Globe,
                t: "Multi-state, multi-DMA campaigns",
                d: "We routinely run campaigns across DC, Richmond, Charlotte, Raleigh, and beyond, coordinated under one brand story.",
              },
              {
                icon: Sparkles,
                t: "Campaign collaboration included",
                d: "We work with destinations on messaging, incorporating VTC brand guidelines, and we help develop marketing initiatives alongside the DMO.",
              },
              {
                icon: TrendingUp,
                t: "Real reporting, real ROI",
                d: "Website clicks, room nights, ticket conversions, social reach, every number is in your monthly report.",
              },
              {
                icon: Smartphone,
                t: "Omni-channel by default",
                d: "Streaming, mobile, paid social, and Social Mirror ads, all tied together so your campaign never feels disconnected.",
              },
            ].map((d, i) => (
              <div
                key={i}
                className="surface p-6"
                data-testid={`differentiator-${i}`}
              >
                <d.icon size={22} className="text-[var(--af-lime)] mb-3" />
                <div className="font-display text-xl mb-2">{d.t}</div>
                <div className="text-sm text-[var(--af-cream)]/70 leading-relaxed">
                  {d.d}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* LEAD FORM */}
      <section
        id="lead-form"
        className="grid-bg px-5 sm:px-8 py-24 border-t border-[var(--af-line)]"
        data-testid="lead-form-section"
      >
        <div className="max-w-6xl mx-auto grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <div className="eyebrow mb-4">Take the First Step</div>
            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mb-6">
              20 minutes.
              <br />
              A real plan.
              <br />
              <span className="text-[var(--af-lime)]">No sales theater.</span>
            </h2>
            <p className="text-base sm:text-lg text-[var(--af-cream)]/75 max-w-md mb-7 leading-relaxed">
              Tell us your destination and your top KPI for the next 12 months.
              We'll come back with which DMAs to target, what campaign concept
              to lead with, and what your numbers should look like.
            </p>
            <ul className="space-y-2.5 text-sm text-[var(--af-cream)]/75">
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Free 20-minute strategy call
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                Multi-state campaign recommendations
              </li>
              <li className="flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[var(--af-lime)]" />
                No obligation, no spam
              </li>
            </ul>
          </div>
          <LeadForm trade="tourism-general" tradeName="Tourism" />
        </div>
      </section>
    </SiteShell>
  );
};

export default TourismHub;
